import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
} from '@mui/material';

import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';

interface UserProfile {
  id: number;
  name: string;
  email: string;
  username: string;
  job_title: string;
  contact_phone: string;
  address_1: string;
  address_2: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  pan_no: string;
  currency: string;
  billing_currency: string;
}

interface ProjectRate {
  id?: number;
  project_id: number;
  project_name: string;
  billing_type: string | null;
  rate: number;
}

interface Project {
  id: number;
  name: string;
}

const ProfileManagement: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [profile, setProfile] = useState<UserProfile>({
    id: 0,
    name: '',
    email: '',
    username: '',
    job_title: '',
    contact_phone: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    pan_no: '',
    currency: '',
    billing_currency: 'USD',
  });
  
  const [projectRates, setProjectRates] = useState<ProjectRate[]>([]);
  const [availableProjects, setAvailableProjects] = useState<Project[]>([]);

  useEffect(() => {
    loadProfile();
  }, [userId]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const endpoint = userId ? `/users/${userId}/profile` : '/profile';
      const response = await api.get(endpoint);
      
      if (response.data.success) {
        const userData = response.data.data.user;
        // Set default billing_currency to USD if empty
        if (!userData.billing_currency) {
          userData.billing_currency = 'USD';
        }
        setProfile(userData);
        setProjectRates(response.data.data.project_rates || []);
        setAvailableProjects(response.data.data.available_projects || []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (field: keyof UserProfile, value: string) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleProjectRateChange = (projectId: number, field: 'billing_type' | 'rate', value: string | number) => {
    setProjectRates(prev => {
      const existing = prev.find(rate => rate.project_id === projectId);
      if (existing) {
        return prev.map(rate => 
          rate.project_id === projectId 
            ? { ...rate, [field]: value }
            : rate
        );
      } else {
        // Create new rate entry
        const project = availableProjects.find(p => p.id === projectId);
        return [...prev, {
          project_id: projectId,
          project_name: project?.name || '',
          billing_type: field === 'billing_type' ? value as string : null,
          rate: field === 'rate' ? Number(value) : 0,
        }];
      }
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const endpoint = userId ? `/users/${userId}/profile` : '/profile';
      const response = await api.put(endpoint, {
        ...profile,
        project_rates: projectRates,
      });
      
      if (response.data.success) {
        setSuccess('Profile updated successfully');
        loadProfile(); // Reload to get updated data
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          {userId ? 'Manage User Profile' : 'My Profile'}
        </Typography>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Personal Information</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField
            fullWidth
            label="Full Name *"
            value={profile.name}
            onChange={(e) => handleProfileChange('name', e.target.value)}
            required
          />
          <TextField
            fullWidth
            label="Email *"
            value={profile.email}
            onChange={(e) => handleProfileChange('email', e.target.value)}
            required
          />
          <TextField
            fullWidth
            label="Username *"
            value={profile.username}
            onChange={(e) => handleProfileChange('username', e.target.value)}
            required
          />
          <TextField
            fullWidth
            label="Job Title *"
            value={profile.job_title}
            onChange={(e) => handleProfileChange('job_title', e.target.value)}
            required
          />
          <TextField
            fullWidth
            label="Contact No *"
            value={profile.contact_phone}
            onChange={(e) => handleProfileChange('contact_phone', e.target.value)}
            required
          />
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Address</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField
            fullWidth
            label="Country *"
            value={profile.country}
            onChange={(e) => handleProfileChange('country', e.target.value)}
            required
          />
          <TextField
            fullWidth
            label="Address 1 *"
            value={profile.address_1}
            onChange={(e) => handleProfileChange('address_1', e.target.value)}
            required
          />
          <TextField
            fullWidth
            label="Address 2"
            value={profile.address_2}
            onChange={(e) => handleProfileChange('address_2', e.target.value)}
          />
          <TextField
            fullWidth
            label="City *"
            value={profile.city}
            onChange={(e) => handleProfileChange('city', e.target.value)}
            required
          />
          <TextField
            fullWidth
            label="State *"
            value={profile.state}
            onChange={(e) => handleProfileChange('state', e.target.value)}
            required
          />
          <TextField
            fullWidth
            label="Zip Code *"
            value={profile.zip_code}
            onChange={(e) => handleProfileChange('zip_code', e.target.value)}
            required
          />
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Project Rates</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Project ID</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Billing Type</TableCell>
                <TableCell>Rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {availableProjects.map((project) => {
                const rate = projectRates.find(r => r.project_id === project.id);
                return (
                  <TableRow key={project.id}>
                    <TableCell>{project.id}</TableCell>
                    <TableCell>{project.name}</TableCell>
                    <TableCell>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={rate?.billing_type || ''}
                          onChange={(e) => handleProjectRateChange(project.id, 'billing_type', e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">--Choose--</MenuItem>
                          <MenuItem value="page_based">Page Based</MenuItem>
                          <MenuItem value="case_based">Case Based</MenuItem>
                          <MenuItem value="time_based">Time Based</MenuItem>
                          <MenuItem value="flat_rate">Flat Rate</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={rate?.rate || 0}
                        onChange={(e) => handleProjectRateChange(project.id, 'rate', parseFloat(e.target.value) || 0)}
                        inputProps={{ step: 0.01, min: 0 }}
                        sx={{ width: 100 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Financial Details</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField
            fullWidth
            label="PAN No"
            value={profile.pan_no}
            onChange={(e) => handleProfileChange('pan_no', e.target.value)}
          />
          <FormControl fullWidth>
            <InputLabel>Currency</InputLabel>
            <Select
              value={profile.currency || ''}
              onChange={(e) => handleProfileChange('currency', e.target.value)}
              label="Currency"
            >
              <MenuItem value="">--Choose--</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
              <MenuItem value="GBP">GBP</MenuItem>
              <MenuItem value="INR">INR</MenuItem>
              <MenuItem value="JPY">JPY</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Billing Currency</InputLabel>
            <Select
              value={profile.billing_currency || 'USD'}
              onChange={(e) => handleProfileChange('billing_currency', e.target.value)}
              label="Billing Currency"
            >
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
              <MenuItem value="GBP">GBP</MenuItem>
              <MenuItem value="INR">INR</MenuItem>
              <MenuItem value="JPY">JPY</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? <CircularProgress size={20} /> : 'Save Changes'}
        </Button>
      </Box>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfileManagement;
